import { ColDef, ColGroupDef } from 'ag-grid-community';
import { BalanceDetail } from './BalanceDetailsGrid';
import { format } from 'erisxkit/client';
import { formatUSD } from '../../../utils/methods';
import { get } from 'lodash';
import moment from 'moment';
import compareNumbers from '../../../utils/agGrid/comparators/compareNumbers';

export type BalanceDetailRow = BalanceDetail & {
  _subRows?: BalanceDetail[];
};

const columnDefs = (
  showCurrenexBalances: boolean,
  cdfEnabled = false,
): ColDef<BalanceDetailRow>[] => [
      {
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          data?.symbol ? `${data?.symbol.toUpperCase()} (${data?.fd})` : '',
        headerName: 'Asset (FD)',
        field: 'symbol',
        width: 120,
      },
      {
        field: 'openingBalance',
        headerName: 'Opening',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.openingBalance) || 0,
        comparator: compareNumbers,
        width: 110,
      },
      {
        field: 'spotMovement',
        headerName: 'Spot Movements',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          data?.spotMovement || '0',
        comparator: compareNumbers,
        width: 140,
      },
      {
        field: 'exchangeFees',
        headerName: 'Ex Fees',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.exchangeFees) || 0,
        comparator: compareNumbers,
        width: 110,
      },
      {
        field: 'clearingFees',
        headerName: 'Cl Fees',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.clearingFees) || 0,
        comparator: compareNumbers,
        width: 110,
      },
      {
        field: 'otherFees',
        headerName: 'Other Fees',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.otherFees) || 0,
        comparator: compareNumbers,
        width: 125,
      },
      {
        field: 'assetMovement',
        headerName: 'Asset Movements',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          data?.assetMovement || '0',
        comparator: compareNumbers,
        initialWidth: 140,
      },
      {
        field: 'realizedPAndL',
        headerName: 'Realized P&L',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          data?.realizedPAndL || '0',
        comparator: compareNumbers,
        width: 140,
      },
      {
        field: 'futuresDelivery',
        headerName: 'Futures Delivery',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          data?.futuresDelivery || '0',
        comparator: compareNumbers,
        initialWidth: 140,
      },
      {
        field: 'variationMargin',
        headerName: 'Variation Margin',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.variationMargin) || 0,
        comparator: compareNumbers,
        width: 145,
      },
      ...(cdfEnabled
        ? [
            {
              headerName: 'Funding Amount',
              cellClass: 'text-right mono',
              valueFormatter: ({ data }: { data?: BalanceDetail }) =>
                format(data?.fundingAmount) || '-',
              comparator: compareNumbers,
              width: 140,
            },
          ]
        : []),
      {
        headerName: 'Unsettled NOV',
        field: 'unsettledNov',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.unsettledNov) || '',
        comparator: compareNumbers,
        width: 140,
      },
      {
        headerName: 'Expiry Cash Movements',
        field: 'exercisedValue',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.exercisedValue) || '',
        comparator: compareNumbers,
        width: 140,
      },
      {
        headerName: 'Closing',
        field: 'closingBalance',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.closingBalance) || 0,
        comparator: compareNumbers,
        width: 110,
      },
      {
        headerName: 'Total Equity',
        field: 'totalEquity',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.totalEquity) || 0,
        comparator: compareNumbers,
        width: 130,
      },
      {
        headerName: 'TME Balance',
        field: 'currenexBalance',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.currenexBalance) || 0,
        width: 130,
      },
      {
        headerName: 'TME Available Balance',
        cellClass: 'text-right mono',
        hide: !showCurrenexBalances,
        field: 'purchasingPower',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.purchasingPower) || 0,
        comparator: compareNumbers,
        initialWidth: 140,
      },
      {
        headerName: 'Initial Margin',
        cellClass: 'text-right mono',
        field: 'initialMargin',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.initialMargin) || 0,
        comparator: compareNumbers,
        width: 130,
      },
      {
        headerName: 'Maintenance Margin',
        cellClass: 'text-right mono',
        field: 'maintenanceMargin',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.maintenanceMargin) || 0,
        comparator: compareNumbers,
        width: 140,
      },
      {
        headerName: 'Pending',
        cellClass: 'text-right mono',
        field: 'pendingBalance',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.pendingBalance) || '',
        comparator: compareNumbers,
        width: 110,
      },
      {
        headerName: 'Total USD value',
        cellClass: 'text-right mono',
        field: 'usdValue',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          data?.usdValue ? formatUSD(data?.usdValue) : '-',
        comparator: compareNumbers,
        width: 160,
      },
      {
        headerName: 'Total Excess/Deficit',
        cellClass: 'text-right mono',
        field: 'totalExcessDeficit',
        valueFormatter: ({ data }: { data?: BalanceDetail }) =>
          format(data?.totalExcessDeficit) || 0,
        comparator: compareNumbers,
        width: 175,
      },
      {
        headerName: 'Settlement Balance',
        cellClass: 'text-right mono',
        valueGetter: ({ data }: { data?: BalanceDetail }) => {
          const settlementBalance = get(data, [
            'settlementDetail',
            'settlementBalance',
          ]);
          return settlementBalance || '-';
        },
        comparator: compareNumbers,
        width: 175,
      },
      {
        headerName: 'Settlement Time',
        cellClass: 'text-right mono',
        valueFormatter: ({ data }: { data?: BalanceDetail }) => {
          const settlementTime = get(data, ['settlementDetail', 'settlementTime']);
          return settlementTime
            ? moment(settlementTime).format('YYYY-MM-DD HH:mm')
            : '-';
        },
        width: 160,
      }
];

export default columnDefs;